import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChevronLeft } from "lucide-react";
import { V } from "@/lib/vocab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-full bg-white border border-sand-200 px-4 py-3 text-sm outline-none focus:border-ink transition-colors";
const errText = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };

const Btn = ({ accent, children, ...rest }) => (
  <button {...rest} style={accent ? { backgroundColor: accent } : undefined}
    className="mt-3 w-full rounded-full bg-ink text-white font-semibold py-3 text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2">{children}</button>
);

function CodeAndPassword({ identifier, masked, method, title, blurb, accent, onDone, resend, testPrefix }) {
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Use at least 8 characters."); return; }
    if (pw !== pw2) { toast.error("Those passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/resident/auth/code/set-password`, { identifier, code, password: pw }, { withCredentials: true });
      onDone(data);
    } catch (err) { toast.error(errText(err, "That didn't work — check the code and try again.")); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid={`${testPrefix}-code-form`}>
      <div className="font-heading font-bold text-ink text-sm">{title}</div>
      <p className="text-sm text-ink-700 mt-1 leading-relaxed">{blurb || <>We {method === "email" ? "emailed" : "texted"} a 6-digit code to <span className="font-semibold">{masked}</span>. Type it here, then pick the password you'll use from now on.</>}</p>
      <input data-testid={`${testPrefix}-code`} inputMode="numeric" autoFocus maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        placeholder="6-digit code" className={`${inputCls} mt-3 text-center text-lg font-bold tracking-[0.4em]`} />
      <input data-testid={`${testPrefix}-new-password`} type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (8+ characters)" className={`${inputCls} mt-2`} />
      <input data-testid={`${testPrefix}-confirm-password`} type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Type it again" className={`${inputCls} mt-2`} />
      <Btn accent={accent} type="submit" disabled={busy || code.length !== 6} data-testid={`${testPrefix}-code-submit`}>{busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save password and sign in"}</Btn>
      {resend && <button type="button" data-testid={`${testPrefix}-resend`} onClick={resend} className="mt-2 w-full text-xs font-semibold text-ink/60 hover:text-ink">Didn't get it? Send another code</button>}
    </form>
  );
}

// Everything a resident does after typing their email or mobile: password, first-time activation
// by code, forgot-password, and the account-number path for a contact we don't have on file.
export function ResidentDoor({ identifier, lookup, accent = "", onBack, onSignedIn, initialStep = "", testPrefix = "res" }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(initialStep || (lookup?.has_password ? "password" : "activate"));
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState({ masked: lookup?.masked || "", method: lookup?.methods?.includes("sms_code") ? "sms" : "email" });
  const [acct, setAcct] = useState({ account_number: "", last_name: "", contact: "" });
  const [acctContact, setAcctContact] = useState("");
  const first = lookup?.first_name ? `, ${lookup.first_name}` : "";

  const finish = (data) => {
    // The session rides the HttpOnly resident_token cookie the server set — nothing is kept in localStorage (iter475).
    toast.success(`Welcome${data?.name ? `, ${data.name.split(" ")[0]}` : ""}!`);
    if (onSignedIn) onSignedIn(data); else navigate("/portal");
  };

  const login = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/resident/auth/password/login`, { identifier, password }, { withCredentials: true });
      finish(data);
    } catch (err) { toast.error(errText(err, "Sign-in failed.")); }
    finally { setBusy(false); }
  };

  const sendCode = async (nextStep = "code") => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/resident/auth/code/send`, { identifier });
      if (!data.method) { setStep("account"); return; }
      setSent({ masked: data.masked, method: data.method }); setStep(nextStep);
      if (!data.sent) toast.info("Code created — in this test environment messages aren't delivered.");
    } catch (err) { toast.error(errText(err, "Couldn't send a code right now.")); }
    finally { setBusy(false); }
  };

  const startAccount = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/resident/auth/activate/account`, acct);
      setAcctContact(acct.contact); setSent({ masked: data.masked, method: data.method }); setStep("account_code");
    } catch (err) { toast.error(errText(err, "We couldn't match that account.")); }
    finally { setBusy(false); }
  };

  const back = <button type="button" data-testid={`${testPrefix}-back`} onClick={onBack} className="mb-3 text-xs font-semibold text-ink/50 hover:text-ink flex items-center gap-1"><ChevronLeft className="h-3.5 w-3.5" /> Different email or number</button>;
  const box = (children) => <div data-testid={`${testPrefix}-door`} className="rounded-2xl border border-sand-200 bg-white p-4">{onBack && step !== "account" && back}{children}</div>;
  const backFromAccount = () => (initialStep === "account" && onBack) ? onBack() : setStep(lookup?.has_password ? "password" : "activate");

  if (step === "password") return box(
    <form onSubmit={login} data-testid={`${testPrefix}-password-form`}>
      <div className="font-heading font-bold text-ink text-sm">Welcome back{first}</div>
      <p className="text-sm text-ink-700 mt-1">Signing in to {lookup?.community || `your ${V.lc("org")}`} as <span className="font-semibold">{identifier}</span>.</p>
      <input data-testid={`${testPrefix}-password`} type="password" autoFocus autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={`${inputCls} mt-3`} />
      <Btn accent={accent} type="submit" disabled={busy || !password} data-testid={`${testPrefix}-login-submit`}>{busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}</Btn>
      <button type="button" data-testid={`${testPrefix}-forgot`} onClick={() => sendCode("reset")} disabled={busy} className="mt-3 w-full text-sm font-semibold text-redline-600 hover:text-redline-700 disabled:opacity-60">Forgot password?</button>
    </form>
  );

  if (step === "activate") return box(
    <div data-testid={`${testPrefix}-activate`}>
      <div className="font-heading font-bold text-ink text-sm">Welcome{first} — let's set your password</div>
      <p className="text-sm text-ink-700 mt-1 leading-relaxed">You're on the {lookup?.community || `${V.lc("org")}`} roster but haven't set a password yet. We'll {sent.method === "email" ? "email" : "text"} a code to <span className="font-semibold">{sent.masked || identifier}</span> to confirm it's you.</p>
      <Btn accent={accent} type="button" disabled={busy} onClick={() => sendCode("code")} data-testid={`${testPrefix}-send-code`}>{busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : `${sent.method === "email" ? "Email" : "Text"} me a code`}</Btn>
      <button type="button" data-testid={`${testPrefix}-use-account`} onClick={() => setStep("account")} className="mt-2 w-full text-xs font-semibold text-ink/60 hover:text-ink">That's not my number any more — use my account number</button>
    </div>
  );

  if (step === "code" || step === "reset") return box(
    <CodeAndPassword identifier={identifier} masked={sent.masked} method={sent.method} accent={accent} onDone={finish} resend={() => sendCode(step)} testPrefix={testPrefix}
      title={step === "reset" ? "Reset your password" : "Confirm it's you"} />
  );

  if (step === "account") return box(
    <form onSubmit={startAccount} data-testid={`${testPrefix}-account-form`}>
      <div className="font-heading font-bold text-ink text-sm">Activate with your account number</div>
      <p className="text-sm text-ink-700 mt-1 leading-relaxed">It's on your statement or your welcome message (like MPL-000123). We'll send a code to the mobile or email you enter so we know it's you.</p>
      <input data-testid={`${testPrefix}-acct-number`} autoFocus value={acct.account_number} onChange={(e) => setAcct({ ...acct, account_number: e.target.value })} placeholder="Account number" className={`${inputCls} mt-3`} />
      <input data-testid={`${testPrefix}-acct-last`} value={acct.last_name} onChange={(e) => setAcct({ ...acct, last_name: e.target.value })} placeholder="Last name" className={`${inputCls} mt-2`} />
      <input data-testid={`${testPrefix}-acct-contact`} value={acct.contact} onChange={(e) => setAcct({ ...acct, contact: e.target.value })} placeholder="Your mobile or email" className={`${inputCls} mt-2`} />
      <Btn accent={accent} type="submit" disabled={busy || !acct.account_number || !acct.last_name || !acct.contact} data-testid={`${testPrefix}-acct-submit`}>{busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking…</> : "Send me a code"}</Btn>
      <button type="button" onClick={backFromAccount} className="mt-2 w-full text-xs font-semibold text-ink/60 hover:text-ink">Back</button>
    </form>
  );

  if (step === "account_code") return box(
    <CodeAndPassword identifier={acctContact} masked={sent.masked} method={sent.method} accent={accent} onDone={finish} testPrefix={testPrefix}
      title="Confirm it's you" resend={null}
      blurb={<>We {sent.method === "email" ? "emailed" : "texted"} a code to <span className="font-semibold">{sent.masked}</span>. Type it, pick a password, and that contact becomes your sign-in from now on.</>} />
  );
  return null;
}
